import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReasonCode } from '../api';
import { reasonMessage } from '../i18n';
import { useSession, useSessionRetry } from '../lib/api/session-context';

/**
 * The opening of the Mini App (specs/S2-auth/spec.md §4): shows the session states in front of
 * the routes and renders `children` only once the trader is recognized.
 *
 * Every failure text is `t(reasonMessage(reasonCode))`: no raw message, no response body (I9,
 * I12). It also replaces the app when the session ends later (a final 401, or `INIT_DATA_EXPIRED`
 * after 24 h at renewal), because no screen can work without a session.
 */
export function SessionGate({ children }: { children: ReactNode }): ReactElement {
  const { t } = useTranslation();
  const session = useSession();
  const retry = useSessionRetry();
  if (session.status === 'ready') return <>{children}</>;
  return (
    <Box
      component="main"
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      {session.status === 'opening' ? (
        // Spec §4: a spinner, no text. The label is for screen readers only (P6).
        <CircularProgress aria-label={t('session.opening')} />
      ) : (
        <SessionFailure
          reasonCode={session.reasonCode}
          retryable={session.retryable}
          onRetry={retry}
        />
      )}
    </Box>
  );
}

function SessionFailure({
  reasonCode,
  retryable,
  onRetry,
}: {
  reasonCode: ReasonCode;
  retryable: boolean;
  onRetry: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const message = reasonMessage(reasonCode);
  return (
    <Alert
      // Outside Telegram nothing failed: the app is just opened in the wrong place.
      severity={reasonCode === 'INIT_DATA_MISSING' ? 'info' : 'error'}
      sx={{ width: '100%', maxWidth: 560 }}
      action={
        retryable ? (
          <Button color="inherit" size="small" onClick={onRetry} sx={{ minHeight: 36 }}>
            {t('session.retry')}
          </Button>
        ) : undefined
      }
    >
      {t(message.key, message.values)}
    </Alert>
  );
}
